import React from "react";
import { Box, FormControlLabel, MenuItem, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import {
  Granularity,
  SaleLine,
  TREND_METRICS,
  TrendMetric,
  autoGranularity,
  buildTrend,
  formatCount,
  formatMoney,
  labelStride,
} from "../salesData";
import { C } from "../../../common/theme";
import { CHART_COLORS, EmptyState, Panel, chartSx, useIsNarrow } from "../../../ui";

const SERIES_COLOR = CHART_COLORS[0];
const PARTIAL_COLOR = C.line2;
const MAX_LABELLED_BARS = 14;
const MARGIN = { left: 56, right: 8, top: 12, bottom: 28 };

/** Width of an element, kept current as the layout changes. */
function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = React.useRef<T | null>(null);
  const [width, setWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

interface SalesTrendProps {
  lines: SaleLine[];
  start: Date;
  end: Date;
  currency: string;
}

export default function SalesTrend({ lines, start, end, currency }: SalesTrendProps) {
  const [metric, setMetric] = React.useState<TrendMetric>("gmv");
  const [granularity, setGranularity] = React.useState<Granularity | "auto">("auto");
  const [cumulative, setCumulative] = React.useState(false);

  const g = granularity === "auto" ? autoGranularity(start, end) : granularity;
  const points = React.useMemo(
    () => buildTrend(lines, start, end, g, metric, cumulative),
    [lines, start, end, g, metric, cumulative]
  );
  const meta = TREND_METRICS.find((m) => m.key === metric)!;
  const format = (v: number | null, compact = false) =>
    v === null ? "–" : meta.money ? formatMoney(v, currency, compact) : formatCount(v);
  const total = cumulative ? points[points.length - 1]?.value ?? 0 : points.reduce((acc, p) => acc + p.value, 0);
  const hasPartial = points.some((p) => p.partial);
  // Keys (not labels) identify the categories so two buckets with the same
  // short label can never collapse into one; labels are applied by the formatter.
  const keys = points.map((p) => p.key);
  const byKey = React.useMemo(() => new Map(points.map((p) => [p.key, p])), [points]);
  const [plotRef, width] = useWidth<HTMLDivElement>();
  const stride = labelStride(points.length, width - MARGIN.left - MARGIN.right, g);
  const xAxisCommon = {
    data: keys,
    tickLabelStyle: { fontSize: 10 },
    valueFormatter: (key: string, ctx: { location: string }) => {
      const point = byKey.get(key);
      if (!point) return "";
      return ctx.location === "tick" ? point.label : `${point.longLabel}${point.partial ? " (in progress)" : ""}`;
    },
    tickLabelInterval: (_: unknown, index: number) => index % stride === 0,
  };

  const narrow = useIsNarrow(600);
  const height = narrow ? 220 : 300;
  const granularities = ["auto", "day", "week", "month"] as const;
  const gLabel = (v: (typeof granularities)[number]) => (v === "auto" ? "Auto" : v[0].toUpperCase() + v.slice(1));

  return (
    <Panel sx={{ height: "100%", boxSizing: "border-box" }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 1 }}>
        <Box sx={{ mr: "auto", minWidth: 0 }}>
          <Typography variant="h6" component="h3" sx={{ fontSize: 15 }}>
            {meta.label} {cumulative ? "(cumulative)" : `by ${g}`}
          </Typography>
          <Typography variant="caption" sx={{ color: C.muted }}>
            {format(total)} in this period
          </Typography>
        </Box>
        {narrow ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 1, width: "100%" }}>
            <TextField select size="small" label="Metric" value={metric} onChange={(e) => setMetric(e.target.value as TrendMetric)}>
              {TREND_METRICS.map((m) => (
                <MenuItem key={m.key} value={m.key}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Group by"
              value={granularity}
              onChange={(e) => setGranularity(e.target.value as Granularity | "auto")}
            >
              {granularities.map((v) => (
                <MenuItem key={v} value={v}>
                  {gLabel(v)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        ) : (
          <>
            <ToggleButtonGroup size="small" exclusive value={metric} onChange={(_, v) => v && setMetric(v)}>
              {TREND_METRICS.map((m) => (
                <ToggleButton key={m.key} value={m.key} sx={{ py: 0.25, px: 1, fontSize: 12, textTransform: "none" }}>
                  {m.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <ToggleButtonGroup size="small" exclusive value={granularity} onChange={(_, v) => v && setGranularity(v)}>
              {granularities.map((v) => (
                <ToggleButton key={v} value={v} sx={{ py: 0.25, px: 1, fontSize: 12, textTransform: "none" }}>
                  {gLabel(v)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </>
        )}
        <FormControlLabel
          control={<Switch size="small" checked={cumulative} onChange={(e) => setCumulative(e.target.checked)} />}
          label={<Typography variant="caption">Cumulative</Typography>}
          sx={{ mr: 0, minHeight: 44 }}
        />
      </Box>

      <Box ref={plotRef} sx={{ minWidth: 0 }}>
        {total === 0 ? (
          <Box sx={{ height, display: "grid", placeItems: "center" }}>
            <EmptyState title="No sales match these filters in this period." />
          </Box>
        ) : cumulative ? (
          <LineChart
            height={height}
            slotProps={{ legend: { hidden: true } }}
            grid={{ horizontal: true }}
            margin={MARGIN}
            xAxis={[{ scaleType: "point", ...xAxisCommon }]}
            yAxis={[{ valueFormatter: (v: number | null) => format(v, true), tickLabelStyle: { fontSize: 10 } }]}
            series={[
              {
                data: points.map((p) => p.value),
                label: meta.label,
                color: SERIES_COLOR,
                area: true,
                showMark: points.length <= 24,
                valueFormatter: (v) => format(v),
              },
            ]}
            sx={{ ...chartSx, "& .MuiAreaElement-root": { fillOpacity: 0.08 }, "& .MuiLineElement-root": { strokeWidth: 2 } }}
          />
        ) : (
          <BarChart
            height={height}
            slotProps={{ legend: { hidden: true } }}
            borderRadius={4}
            grid={{ horizontal: true }}
            margin={MARGIN}
            barLabel={(item, { bar }) =>
              points.length <= MAX_LABELLED_BARS && item.value && bar.height >= 18 && bar.width >= 34 && !points[item.dataIndex]?.partial
                ? format(item.value, true)
                : null
            }
            xAxis={[
              {
                scaleType: "band",
                ...xAxisCommon,
                colorMap: {
                  type: "ordinal",
                  values: keys,
                  colors: points.map((p) => (p.partial ? PARTIAL_COLOR : SERIES_COLOR)),
                },
              },
            ]}
            yAxis={[{ valueFormatter: (v: number | null) => format(v, true), tickLabelStyle: { fontSize: 10 } }]}
            series={[
              {
                data: points.map((p) => p.value),
                label: meta.label,
                color: SERIES_COLOR,
                valueFormatter: (v) => format(v),
              },
            ]}
            sx={{ ...chartSx, "& .MuiBarLabel-root": { fontSize: 10, fontWeight: 600, fill: C.paper } }}
          />
        )}
      </Box>
      {hasPartial && !cumulative && total > 0 && (
        <Typography variant="caption" sx={{ color: C.muted }}>
          The lighter bar is the current {g}, still in progress.
        </Typography>
      )}
    </Panel>
  );
}
